import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./styles/Streaktrends.css";
import fire from "../assets/fire.png";
import openNotification from "components/utils/CustomNotification";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

interface ApiDayEntry {
  date: string;
  org_streak_value: number;
  self_streak_value: number;
  self_streak_achieved?: boolean;
  org_streak_achieved?: boolean;
  selected_value?: number;
}

interface ApiResponse {
  type: string;
  status: string;
  data: {
    user_uid: string;
    from_date: string;
    to_date: string;
    data: ApiDayEntry[];
  };
}

const Streaktrends: React.FC = () => {
  useCheckSessionHeartBeat();
  const userUid = localStorage.getItem("user_uid") || "";
  const sessionUid = localStorage.getItem("session_uid") || "";
  const BASE_URL =
    (window as any)._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const [weeklyStreak, setWeeklyStreak] = useState<boolean[]>([]);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [calendarData, setCalendarData] = useState<ApiDayEntry[]>([]);
  const [viewMode, setViewMode] = useState<"weekly" | "monthly">("weekly");
  const [loading, setLoading] = useState(true);
  const [activeMonth, setActiveMonth] = useState(new Date());
  const [filterType, setFilterType] = useState<"self" | "org">("self");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const today = new Date();
  const sixDaysAgo = new Date();
  sixDaysAgo.setDate(today.getDate() - 7);

  // const getMonthDateRange = (date: Date) => {
  //   const y = date.getFullYear();
  //   const m = date.getMonth();

  //   const from_date = `${y}-${String(m + 1).padStart(2, "0")}-01T00:00:00`;

  //   const lastDay = new Date(y, m + 1, 0);
  //   const to_date = `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}T00:00:00`;

  //   return { from_date, to_date };
  // };
  const getMonthDateRange = (date: Date) => {
    const y = date.getFullYear();
    const m = date.getMonth();

    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === y && today.getMonth() === m;

    const from_date = `${y}-${String(m + 1).padStart(2, "0")}-01T00:00:00`;

    let to_date = "";

    if (isCurrentMonth) {
      // Use today's date
      to_date = `${y}-${String(m + 1).padStart(2, "0")}-${String(
        today.getDate()
      ).padStart(2, "0")}T00:00:00`;
    } else {
      // Use last day of the month
      const lastDay = new Date(y, m + 1, 0);
      to_date = `${y}-${String(m + 1).padStart(2, "0")}-${String(
        lastDay.getDate()
      ).padStart(2, "0")}T00:00:00`;
    }

    return { from_date, to_date };
  };




  // Format date YYYY-MM-DDT00:00:00Z
  const formatToLocalISO = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    // Always 00:00:00
    return `${year}-${month}-${day}T00:00:00`;
  };




  // Calculate current streak from consecutive days
  const calculateCurrentStreak = (data: { date: string; value: number }[]): number => {
    let streak = 0;
    const sortedData = [...data].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    for (const day of sortedData) {
      if (day.value > 0) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const fetchStreakData = useCallback(
    async (fromDate: string, toDate: string) => {
      try {
        setLoading(true);
        const response = await axios.get<ApiResponse>(
          `${BASE_URL}/api/user-streak-trend`,
          {
            headers: {
              "user-uid": userUid,
            },
            withCredentials: true,
            params: {
              from_date: fromDate,
              to_date: toDate,
            },
          }
        );

        if (response.data.status === "Success") {
          const apiData = response.data.data.data;
          const streakKey =
            filterType === "self" ? "self_streak_value" : "org_streak_value";

          const last7 = apiData.slice(-7);
          const week = last7.map((d) =>
            filterType === "self"
              ? !!d.self_streak_achieved
              : !!d.org_streak_achieved
          );
          setWeeklyStreak(week);

          setCurrentStreak(
            calculateCurrentStreak(
              apiData.map(day => ({
                date: day.date,
                value: day[streakKey]
              }))
            )
          );

          setCalendarData(
            apiData.map(day => ({
              ...day,
              selected_value:
                filterType === "self"
                  ? day.self_streak_value
                  : day.org_streak_value
            }))
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [filterType, BASE_URL, sessionUid, userUid]
  );


  useEffect(() => {
    const fromDate = formatToLocalISO(sixDaysAgo);
    const toDate = formatToLocalISO(today);
    fetchStreakData(fromDate, toDate);
  }, []);

  useEffect(() => {
    if (viewMode === "weekly") {
      fetchStreakData(formatToLocalISO(sixDaysAgo), formatToLocalISO(today));
    } else {
      const { from_date, to_date } = getMonthDateRange(activeMonth);
      fetchStreakData(from_date, to_date);
    }
  }, [filterType, viewMode, activeMonth]);


  const handleToggle = () => {
    setIsTransitioning(true);

    setTimeout(() => {
      const newMode = viewMode === "weekly" ? "monthly" : "weekly";
      setViewMode(newMode);

      if (newMode === "weekly") {
        fetchStreakData(formatToLocalISO(sixDaysAgo), formatToLocalISO(today));
      } else {
        const { from_date, to_date } = getMonthDateRange(activeMonth);
        fetchStreakData(from_date, to_date);
      }

      setIsTransitioning(false);
    }, 300);
  };

  const handleFilterChange = (type: "self" | "org") => {
    if (type === filterType) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setFilterType(type);

      if (viewMode === "weekly") {
        fetchStreakData(formatToLocalISO(sixDaysAgo), formatToLocalISO(today));
      } else {
        const { from_date, to_date } = getMonthDateRange(activeMonth);
        fetchStreakData(from_date, to_date);
      }

      setIsTransitioning(false);
    }, 300);
  };

  // Convert API date string to comparable YYYY-MM-DD string
  const normalizeDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const weekDayLabels = ["M", "T", "W", "T", "F", "S", "S"];

  if (loading && calendarData.length === 0) {
    return (
      <div className="streak-trends-page">
        <div className="streak-card-full">
          <div className="streak-loading">
            <div className="skeleton-header">
              <div className="skeleton-streak-info">
                <div className="skeleton-box skeleton-circle"></div>
                <div className="skeleton-box skeleton-text"></div>
              </div>
              <div className="skeleton-box skeleton-toggle"></div>
            </div>
            {viewMode === "weekly" ? (
              <div className="skeleton-week">
                {[...Array(7)].map((_, i) => (
                  <div key={i} className="skeleton-box skeleton-day"></div>
                ))}
              </div>
            ) : (
              <div className="skeleton-box skeleton-calendar"></div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="streak-trends-page">
      <div className="streak-card-full">
        <div className="streak-main-header">
          <div className="streak-header">
            <div className="streak-icon-wrapper">
              <img src={fire} alt="fire" className="fire-icon" />
            </div>
            <div className="streaktrends-streak-info">
              {/* <div className="streak-count">{currentStreak}</div> */}
              <div className="streak-label">Streak</div>
            </div>
          </div>

          <div className="streak-filter-container">
            <button
              className={`filter-option ${filterType === "self" ? "active" : ""}`}
              onClick={() => handleFilterChange("self")}
            >
              Self
            </button>
            <button
              className={`filter-option ${filterType === "org" ? "active" : ""}`}
              onClick={() => handleFilterChange("org")}
            >
              Org
            </button>
          </div>

          <div className="view-toggle-container">
            <span className={`toggle-label ${viewMode === "weekly" ? "active" : ""}`}>
              Weekly
            </span>
            <div className="toggle-switch" onClick={handleToggle}>
              <input
                type="checkbox"
                checked={viewMode === "monthly"}
                onChange={handleToggle}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
            <span className={`toggle-label ${viewMode === "monthly" ? "active" : ""}`}>
              Monthly
            </span>
          </div>
        </div>

        <div
          className="streak-content-area"
          style={{
            opacity: isTransitioning ? 0 : 1,
            transition: 'opacity 0.3s ease'
          }}
        >
          {viewMode === "weekly" ? (
            <div className="streak-week-container">
              <div className="streak-week-row">
                {weeklyStreak.map((done, i) => {
                  const d = new Date();
                  d.setDate(today.getDate() - (6 - i));

                  // Format: 02 Jan 25
                  const formattedDate = d.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "2-digit",
                  });

                  return (
                    <div key={i} className="day-column">
                      <div className={`day-dot ${done ? "completed" : "incomplete"}`}>
                        {done && <span className="check-icon">✓</span>}
                      </div>

                      <div className="week-day-info">
                        <span className="day-label">
                          {d.toLocaleDateString("en-US", { weekday: "short" }).charAt(0)}
                        </span>

                        <span className="day-date">{formattedDate}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          ) : (
            <div className="calendar-wrapper-inline">
              <Calendar
                locale="en-GB"
                activeStartDate={activeMonth}
                onActiveStartDateChange={({ activeStartDate }) => {
                  if (activeStartDate) {
                    setActiveMonth(activeStartDate);

                    const { from_date, to_date } = getMonthDateRange(activeStartDate);
                    fetchStreakData(from_date, to_date);
                  }
                }}
                view="month"
                className="streak-calendar"
                tileClassName={({ date, view }) => {
                  if (view !== "month") return "";

                  const match = calendarData.find(
                    (d) => d.date.split("T")[0] === normalizeDate(date)
                  );

                  if (match && (match.selected_value ?? 0) > 0) {
                    return "has-streak";
                  }

                  return "";
                }}
                tileContent={({ date, view }) => {
                  if (view !== "month") return null;

                  const match = calendarData.find(
                    (d) => d.date.split("T")[0] === normalizeDate(date)
                  );

                  if (match && (
                    filterType === "self"
                      ? match.self_streak_achieved
                      : match.org_streak_achieved
                  )
                  ) {
                    return (
                      <div
                        className="calendar-tile-content"
                        title={`Streak: ${match.selected_value}`}
                      >
                        <img src={fire} alt="streak" className="calendar-fire-icon" />
                      </div>
                    );
                  }

                  return null;
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Streaktrends;
